/* ============================================================
 * src/screens/auth/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-auth.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { lastAccount, forgetAccount } from '../../lib/lastAccount';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
import { Wordmark } from '../../layouts/Shell';

// T319 / FR-023 — Prototype fixture importer retired. This bundle is the
// public (pre-auth) sign-in / sign-up / verify / forgot-password / onboarding
// surface; it MUST NOT call any authenticated Convex subscription, and the
// post-WIRE-0 honest-state contract (specs/prd.md §E.3 + honest-state.test.ts
// H5) forbids fabricated fixture-data reads. The previous
// `CIRCA.counts.live` decorative count on the brand panel violated both
// rules and has been replaced with static brand copy that does not claim
// a fact about runtime state. The closure-guard scan defined by
// tests/destub/stub-refs.test.ts (production-tree files only; *.test.*
// files exempt) is now empty for this directory.

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;

/* ============================================================
   CIRCA — Auth & onboarding screens (public, no shell)
   ============================================================ */

function AuthShell({ children, width = 440 }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--canvas)' }}>
      {/* Brand panel — deep-forest-green (was purple) */}
      <div className="circa-auth-aside" style={{ width: '44%', maxWidth: 620, flexShrink: 0, background: 'linear-gradient(155deg, var(--forest-a), var(--forest-b))',
        padding: '56px 56px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -80, top: -80, width: 380, height: 380, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'absolute', left: -60, bottom: 40, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'baseline', gap: 6, fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 500, color: '#fff' }}>
          Circa<span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--live)', alignSelf: 'flex-end', marginBottom: 7 }} />
        </div>
        <div style={{ position: 'relative' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 38, fontWeight: 400, color: '#fff', letterSpacing: '-0.8px', lineHeight: 1.1 }}>Tu canal, al aire 24×7.</h2>
          <p style={{ margin: '18px 0 28px', fontSize: 16, color: 'rgba(255,255,255,0.78)', lineHeight: 1.55, maxWidth: 400 }}>Programa, transmite y monitorea cada canal desde un solo lugar. Sin servidores que administrar.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[['radio', 'Transmisión continua a YouTube o tu propio reproductor'], ['calendar', 'Programación visual con cortes de anuncio'], ['gauge', 'Monitoreo de señal y calidad en vivo']].map(([ic, t]) => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={ic} size={16} color="#fff" /></span>
                <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--live)', animation: 'circa-pulse 1.4s infinite' }} />
          Transmisión continua para cada audiencia.
        </div>
      </div>
      {/* Form pane */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 28, padding: '40px 24px' }}>
        <div className="circa-auth-logo" style={{ display: 'none' }}><Wordmark size={30} onClick={() => navigate('/sign-in')} /></div>
        <div style={{ width: '100%', maxWidth: width }}>{children}</div>
      </div>
    </div>
  );
}

export function SignIn() {
  const { loginWithRedirect, error, isLoading } = useAuth0();
  const location = useLocation();

  // NMW-1239 — credentials are Universal Login's job now; nothing on this page
  // reads a password. The route the shell gate stashed in location.state.from
  // (Shell.tsx) rides through the redirect in `appState.returnTo` and is put
  // back by `restoreRouteAfterLogin` in main.tsx, so a deep link survives the
  // login instead of landing everyone on `/`.
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const signIn = (prompt?: 'login') => {
    void loginWithRedirect({
      appState: { returnTo: from },
      ...(prompt ? { authorizationParams: { prompt } } : {}),
    });
  };

  // NMW-1239 — who signed in here last (src/lib/lastAccount.ts). Auth0 keeps
  // its own SSO session, so the usual outcome of pressing the button is a
  // silent round trip with no form shown; naming the account turns that from
  // unnerving into obvious. A convenience only — Auth0's session, not this
  // string, decides who actually gets in, so the button still just starts a
  // login.
  const remembered = lastAccount();
  const useAnotherAccount = () => {
    forgetAccount();
    // `prompt: 'login'` makes Auth0 re-authenticate instead of reusing the
    // session, which is the only way to reach the form from a browser that
    // already has one.
    signIn('login');
  };

  return (
    <AuthShell>
      <Panel pad={0} noBorder style={{ background: 'transparent' }}>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400, letterSpacing: '-0.3px', lineHeight: 1.2 }}>Inicia sesión en Circa.</h1>
        <p style={{ margin: '8px 0 28px', fontSize: 16, color: 'var(--muted-slate)' }}>Continúa gestionando tus canales.</p>
        {/* A sign-in that came back rejected (the tenant's post-login gate
            denying a user who holds none of the circa-* roles, a callback URL
            mismatch) lands here with `error` set. Without this the operator
            sees the same button again and cannot tell a denial from a hiccup;
            the gate's own message says whom to contact. */}
        {error ? (
          <p role="alert" style={{ margin: '0 0 18px', fontSize: 14, color: 'var(--danger, #b4232a)' }}>
            No se pudo completar el inicio de sesión: {error.message}
          </p>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Btn variant="solid" size="lg" full disabled={isLoading} onClick={() => signIn()}>
            {remembered ? `Continuar como ${remembered}` : 'Iniciar sesión'}
          </Btn>
          {/* No per-connection button. Universal Login already presents the
              database form AND "Continue with Google" on one screen, so a
              Google button here only pre-selects a connection — saving a click
              in exchange for asking the same question twice, on two screens
              that look nothing alike. Google remains available because
              auth0_connection_client.console_google attaches it to this client;
              that is what puts it on Auth0's screen, and removing this button
              does not remove it there. */}
          {/* Shown ONLY after a failed attempt, which is when it is needed and
              when it reads as an answer rather than a question. A failure is
              also the one moment the console can be sure Auth0 reused a session
              the operator may not have chosen: the attempt round-tripped and
              came back refused without ever showing a login form.
              (An earlier revision gated this on having a remembered account,
              which hid it in exactly that case — nothing is remembered until a
              login succeeds.) */}
          {error ? (
            <Btn variant="ghost" size="lg" full disabled={isLoading} onClick={useAnotherAccount}>
              Usar otra cuenta
            </Btn>
          ) : null}
        </div>
      </Panel>
    </AuthShell>
  );
}

/**
 * NMW-1239 — the retired credential screens.
 *
 * Registration, email verification and password reset all belong to Auth0's
 * Universal Login now: the database connection is `disable_signup`, so an
 * operator is created by an administrator (or by the migration), and the reset
 * link comes from the tenant. These three routes are still mounted
 * (src/routes.tsx) because bookmarks and old invitation emails point at them —
 * so they say what is true and offer the one way forward, rather than
 * rendering a form whose submit handler no longer exists.
 */
function RetiredCredentialScreen({ title, body }: { title: string; body: string }) {
  const { loginWithRedirect } = useAuth0();
  return (
    <AuthShell>
      <Panel pad={0} noBorder style={{ background: 'transparent' }}>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400, letterSpacing: '-0.3px', lineHeight: 1.2 }}>{title}</h1>
        <p style={{ margin: '8px 0 28px', fontSize: 16, color: 'var(--muted-slate)' }}>{body}</p>
        <Btn variant="solid" size="lg" full onClick={() => { void loginWithRedirect({ appState: { returnTo: '/' } }); }}>Ir al inicio de sesión</Btn>
      </Panel>
    </AuthShell>
  );
}

export function SignUp() {
  return (
    <RetiredCredentialScreen
      title="El registro es por invitación."
      body="Las cuentas de Circa las crea un administrador. Si ya tienes una, inicia sesión; si no, pide que te habiliten."
    />
  );
}

export function VerifyEmail() {
  return (
    <RetiredCredentialScreen
      title="Verificación por correo."
      body="Auth0 envía y verifica el correo. Abre el enlace que recibiste y luego inicia sesión aquí."
    />
  );
}

export function ForgotPassword() {
  return (
    <RetiredCredentialScreen
      title="Restablecer la contraseña."
      body="El restablecimiento se hace desde el formulario de Auth0: entra a iniciar sesión y usa «¿Olvidaste tu contraseña?»."
    />
  );
}

export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [broadcast, setBroadcast] = useState<string[]>([]);
  const opts1 = ['Noticias','Música','Deportes','Entretenimiento','Documental','Educación','Otro'];
  const [count, setCount] = useState('2–5');
  const [dest, setDest] = useState<string[]>([]);
  const toggle = (arr: string[], set: (next: string[]) => void, v: string) =>
    set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  const Chip = ({ on, children, onClick }) => <button onClick={onClick} style={{ border: `1px solid ${on ? 'var(--ink-muted)' : 'var(--border-cool)'}`,
    background: on ? 'var(--ink-muted)' : 'transparent', color: on ? '#fff' : 'var(--ink-muted)', borderRadius: 999, padding: '8px 16px', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>{children}</button>;
  return (
    <AuthShell width={720}>
      <Panel pad={0} noBorder style={{ background: 'transparent' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
          {[1,2,3,4].map(s => <div key={s} style={{ flex: 1, height: 5, borderRadius: 999, background: s <= step ? 'var(--ink)' : 'var(--lightest-gray)' }} />)}
        </div>
        {step === 1 && <>
          <h2 style={{ margin: '0 0 20px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>¿Qué vas a transmitir?</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>{opts1.map(o => <Chip key={o} on={broadcast.includes(o)} onClick={() => toggle(broadcast, setBroadcast, o)}>{o}</Chip>)}</div>
        </>}
        {step === 2 && <>
          <h2 style={{ margin: '0 0 20px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>¿Cuántos canales planeas operar?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>{['1','2–5','6–20','20+'].map(o => <button key={o} onClick={() => setCount(o)} style={{ border: `1px solid ${count === o ? 'var(--deep-green)' : 'var(--border-cool)'}`, background: count === o ? 'var(--pale-green)' : 'transparent', borderRadius: 16, padding: '24px 0', fontSize: 20, fontFamily: 'var(--font-display)', cursor: 'pointer' }}>{o}</button>)}</div>
        </>}
        {step === 3 && <>
          <h2 style={{ margin: '0 0 8px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>¿A dónde transmites?</h2>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--muted-slate)' }}>Solo informativo; el destino real se configura por canal.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>{['YouTube Live','Mi propio reproductor o sitio','Ambos'].map(o => <Chip key={o} on={dest.includes(o)} onClick={() => toggle(dest, setDest, o)}>{o}</Chip>)}</div>
        </>}
        {step === 4 && <>
          <h2 style={{ margin: '0 0 8px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>Crea tu primer canal</h2>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--muted-slate)' }}>Un canal es una transmisión 24×7. Puedes ajustarlo después.</p>
          <Field label="Nombre del canal"><Input placeholder="Mi primer canal" /></Field>
        </>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
          <Btn variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : navigate('/sign-in')}>{step > 1 ? 'Atrás' : 'Cancelar'}</Btn>
          <Btn variant="solid" iconRight="arrowRight" onClick={() => step < 4 ? setStep(step + 1) : navigate('/')}>{step < 4 ? 'Continuar' : 'Entrar a Circa'}</Btn>
        </div>
      </Panel>
    </AuthShell>
  );
}

